import { Toast } from 'vant';
import i18n from '@/i18n';
import dayjs from 'dayjs';
import { PAY_METHOD, RENTAL_CYCLE_DAYS } from '@/common/constants';
import { cancelOrder, queryRepayInfoPreview, repay } from '@/api/order';

const addMoney = (...amounts) => amounts.reduce((total, amount) => total + Math.round((Number(amount) || 0) * 100), 0) / 100;
const state = {
  checkoutPayMethod: '',
  tempOrderData: {
    addressId: '', //地址id
    depositAmount: 0, //押金
    riskAmount: 0, //风险金
    firstPayAmount: 0, //首付
    goodsSkuId: '', //商品skuId
    planIndex: 0, // 期数
    planPrice: 0, // 每期租金
    planUnit: '', // 期数单位
    backDate: '', //归还时间
    rentDate: '', // 起租日
    address: null,
    goodsInfo: null,
    sku: null,
    skuStr: '',
    rentalCycleDays: 0,
    payMethod: PAY_METHOD.ONLINE,
  },
  overdueStatus: 'N',  // 逾期金额
  repayAmountTotal: 0, // 最近应还
  emergencyContactList: []
}

const mutations = {
  SET_TEMP_ORDER_DATA(state, params = {}) {
    state.tempOrderData = params;
  },
  CHANGE_ADDRESS(state, params = {}) {
    state.tempOrderData.address = params || null;
    state.tempOrderData.addressId = params?.id || '';
  },
  CHANGE_CONTACT_LIST(state, params = []) {
    state.tempOrderData.emergencyContactList = params;
    state.emergencyContactList = params;
  },
  SET_OVERDUE_STATUS(state, params = 'N') {
    state.overdueStatus = params;
  },
  SET_REPAY_AMOUNT_TOTAL(state, params = 0) {
    state.repayAmountTotal = params;
  },
  CLEAR_REPAY_SUMMARY(state) {
    state.overdueStatus = 'N';
    state.repayAmountTotal = 0;
  },
  CLEAR_PRIVATE_STATE(state) {
    state.checkoutPayMethod = '';
    state.tempOrderData = {
      addressId: '',
      depositAmount: 0,
      riskAmount: 0,
      firstPayAmount: 0,
      firstRentalAmount: 0,
      goodsSkuId: '',
      planIndex: 0,
      rentalTerm: 0,
      planPrice: 0,
      planUnit: '',
      backDate: '',
      rentDate: '',
      address: null,
      goodsInfo: null,
      sku: null,
      skuStr: '',
      rentalCycleDays: 0,
      payMethod: PAY_METHOD.ONLINE,
      emergencyContactList: []
    };
    state.emergencyContactList = [];
    state.overdueStatus = 'N';
    state.repayAmountTotal = 0;
  },
  SET_CHECKOUT_PAY_METHOD(state, params = '') {
    state.checkoutPayMethod = params;
  },
}

const actions = {
  createTempOrder({ commit }, params = {}) {
    let addressId = params.address?.id || '';
    let { id, depositPrice, riskPrice, rentalTerm, price, rentalCycle, } = params.sku || {};
    let firstRentalAmount = addMoney(price, riskPrice);
    let totalPrice = addMoney(firstRentalAmount, depositPrice);
    let rentalCycleDays = RENTAL_CYCLE_DAYS[rentalCycle] || 0;
    let rentDate = dayjs().add(1, 'day').valueOf();
    let backDate;
    if (rentalCycle === 'MONTHLY') {
      backDate = dayjs().add(rentalTerm, 'month').add(1, 'day').valueOf();
    } else {
      backDate = dayjs().add(((rentalCycleDays * rentalTerm) + 1), 'day').valueOf();
    }
    let tempOrderData = {
      addressId: addressId, //地址id
      depositAmount: depositPrice || 0, //押金
      riskAmount: riskPrice || 0, //风险金
      firstPayAmount: totalPrice || 0, //首付
      firstRentalAmount,
      goodsSkuId: id || 0, //商品skuId
      planIndex: rentalTerm || 0, // 期数
      rentalTerm: rentalTerm || 0, // 期数
      planPrice: price || 0, // 每期租金
      planUnit: rentalCycle || '', // 期数单位
      backDate: backDate, //归还时间
      rentDate: rentDate, // 起租日
      address: params.address || null,
      goodsInfo: params.goodsDetail.goodsInfo,
      sku: params.sku,
      skuStr: params.skuStr,
      emergencyContactList: [],
      payMethod: PAY_METHOD.ONLINE,
    }
    console.log('生成订单数据:', tempOrderData);

    commit('SET_TEMP_ORDER_DATA', tempOrderData);
    return tempOrderData;
  },
  changeAddress({ commit }, params = {}) {
    commit('CHANGE_ADDRESS', params);
    return true;
  },
  changeContactList({ commit }, params = {}) {
    commit('CHANGE_CONTACT_LIST', params);
    return true;
  },
  clearTempOrder({ commit }) {
    commit("SET_TEMP_ORDER_DATA", {
      addressId: '', //地址id
      depositAmount: 0, //押金
      riskAmount: 0, //风险金
      firstPayAmount: 0, //首付
      firstRentalAmount: 0,
      goodsSkuId: '', //商品skuId
      planIndex: 0, // 期数
      planPrice: 0, // 每期租金
      planUnit: '', // 期数单位
      backDate: '', //归还时间
      rentDate: '', // 起租日
      address: null,
      goodsInfo: null,
      sku: null,
      skuStr: '',
      rentalCycleDays: 0,
      payMethod: PAY_METHOD.ONLINE,
      emergencyContactList: []
    })
  },
  cancelOrder({ state }, params = {}) {
    return new Promise((resolve, reject) => {
      cancelOrder(params).then(res => {
        if (res.code === 'Success') {
          Toast(i18n.t('orderList.cancelSuccess'));
          resolve(res);
        } else {
          Toast(res?.message || i18n.t('orderList.cancelFailed'));
          reject(res);
        }
      }).catch(err => {
        Toast(i18n.t('orderList.cancelFailed'));
        reject(err);
      })
    })
  },
  queryRepayInfoPreview({ commit }, params) {
    return new Promise((resolve, reject) => {
      if (params) {
        params = {
          pageNum: 1,
          pageSize: 1
        }
      }
      queryRepayInfoPreview(params).then(res => {
        if (res.code === 'Success') {
          commit('SET_OVERDUE_STATUS', res.data.overdueStatus || 'N');
          commit('SET_REPAY_AMOUNT_TOTAL', res.data.repayAmountTotal || 0);
        }
        resolve(res);
      }).catch(err => {
        reject(err);
      })
    })
  },
  doRepay({ }, params) {
    return new Promise((resolve, reject) => {
      repay({
        periods: params.periods,
        rentalOrderId: params.orderId,
        amount: params.amount,
        returnUrl: params.returnUrl,
        payChannel: params.payChannel,
      }).then((res) => {
        resolve(res);
      }).catch(err => {
        Toast(i18n.t('systemError'));
        resolve(null);
      });
    })
  }
}


export default {
  namespaced: true, // 启用命名空间
  state,
  mutations,
  actions
}
